/**
 * 말풍선 (DESIGN_SPEC 6장).
 *
 * - MemberMessage: 아바타 + 이름 + 말풍선(마루 부리). 말한 쪽 위 모서리 6. 말풍선은 화면 폭의 3/5 까지(max-w-bubble).
 *   compact 는 게시판 댓글(패딩 8×12). 근거 칩과 토스 카드는 children 으로 붙입니다.
 * - MyMessage: 오른쪽 정렬, --color-surface-sunken, Pretendard. onNote 를 주면 길게 누르기(400ms) 메뉴
 *   「이모 수첩에 적기」 · 「복사」(시안 13 N6, C-31). 스크린 리더에는 말풍선의 보조 동작 버튼으로.
 *
 * 스크린 리더는 화자를 먼저 읽습니다: "순례 이모가 말함: …" (DESIGN_SPEC 9장).
 * 스트리밍 중에는 aria-busy 로 다 읽히기 전에 끊어 읽지 않게 합니다.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleAlert, Copy, NotebookPen, RotateCcw } from 'lucide-react';
import { Avatar } from './Avatar.jsx';
import { withJosa } from '@shared/korean.js';

export function MemberMessage({
    memberKey,
    name,
    avatarSize = 32,
    compact = false,
    showName = true,
    // 같은 멤버 말풍선이 이어지면 초상 자리만 비움(시안 14 L6)
    showAvatar = true,
    streaming = false,
    children = null,
    body,
}) {
    return (
        <div className="flex items-start gap-2 motion-safe:animate-message-in">
            {showAvatar ? (
                <Avatar memberKey={memberKey} size={avatarSize} />
            ) : (
                <span aria-hidden="true" className="shrink-0" style={{ width: avatarSize }} />
            )}
            <div className="flex max-w-[84%] min-w-0 flex-col gap-1.5">
                {showName ? (
                    <span aria-hidden="true" className="text-label">
                        {name}
                    </span>
                ) : null}
                <p
                    aria-busy={streaming || undefined}
                    className={`m-0 max-w-bubble self-start rounded-bubble rounded-tl-bubble-tail border border-hairline bg-surface font-serif text-body-member break-words whitespace-pre-wrap ${
                        compact ? 'px-3 py-2' : 'px-3.5 py-2.5'
                    }`}
                >
                    <span className="sr-only">{withJosa(name, '이', '가')} 말함: </span>
                    {body}
                    {streaming ? <StreamingCaret /> : null}
                </p>
                {children}
            </div>
        </div>
    );
}

function StreamingCaret() {
    return (
        <span
            aria-hidden="true"
            className="ml-0.5 inline-block h-[1em] w-0.5 translate-y-[2px] bg-text-secondary motion-safe:animate-typing-dot"
        />
    );
}

/**
 * 보내는 중이면 불투명도 0.6. 실패하면(시안 04 D1d) 말풍선 .7 + 왼쪽 느낌표 18 + 아래 「못 보냈어요」 ·
 * 「다시 보내기」(높이 32, 1px brand, 반경 16) · 「지우기」. 멤버 쪽 실패는 보이지 않습니다.
 * @param {'sent'|'sending'|'failed'} status
 */
// 말 앞 @ 멘션(「@손 팀장」, 시안 12 M3b): --brand 600 + 본문
const LEADING_MENTIONS = /^((?:@(?:순례 이모|이모|오름|손 팀장|은별|도 대표)\s)+)/;

function MentionedBody({ body }) {
    const match = LEADING_MENTIONS.exec(String(body ?? ''));
    if (!match) {
        return body;
    }
    return (
        <>
            <span className="font-semibold text-brand">{match[1].trim()}</span> {String(body).slice(match[1].length)}
        </>
    );
}

const LONG_PRESS_MS = 400;
const MOVE_TOLERANCE = 8;

/** 내 말 길게 누르기: 400ms 를 채우면 열림. 움직이거나 떼면 취소. 마우스 오른쪽 클릭도 */
function useLongPress(onLongPress) {
    const timerRef = useRef(null);
    const startRef = useRef(null);
    useEffect(() => () => clearTimeout(timerRef.current), []);
    if (!onLongPress) {
        return {};
    }
    function cancel() {
        clearTimeout(timerRef.current);
        timerRef.current = null;
    }
    return {
        onPointerDown(event) {
            if (event.button !== 0) {
                return;
            }
            startRef.current = { x: event.clientX, y: event.clientY };
            const target = event.currentTarget;
            cancel();
            timerRef.current = setTimeout(() => onLongPress(target), LONG_PRESS_MS);
        },
        onPointerMove(event) {
            const start = startRef.current;
            if (
                start &&
                (Math.abs(event.clientX - start.x) > MOVE_TOLERANCE ||
                    Math.abs(event.clientY - start.y) > MOVE_TOLERANCE)
            ) {
                cancel();
            }
        },
        onPointerUp: cancel,
        onPointerLeave: cancel,
        onPointerCancel: cancel,
        onContextMenu(event) {
            event.preventDefault();
            cancel();
            onLongPress(event.currentTarget);
        },
    };
}

/** 길게 누르기 메뉴(폭 200, 반경 14): 딤 32% + 그 말풍선 아래 */
function MyMessageMenu({ anchor, body, onNote, onClose }) {
    const firstRef = useRef(null);
    const rect = anchor.getBoundingClientRect();
    const top = Math.min(rect.bottom + 8, window.innerHeight - 120);
    const right = Math.max(16, window.innerWidth - rect.right);
    useEffect(() => {
        firstRef.current?.focus();
        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                onClose();
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);
    return createPortal(
        <div className="fixed inset-0 z-50" onClick={onClose}>
            <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
            <div
                role="menu"
                aria-label="내 말"
                style={{ top, right }}
                onClick={(event) => event.stopPropagation()}
                className="absolute flex w-[200px] flex-col overflow-hidden rounded-[14px] border border-black/[.14] bg-surface p-1 motion-safe:animate-message-in"
            >
                <button
                    ref={firstRef}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                        onClose();
                        onNote();
                    }}
                    className="flex h-11 items-center gap-2 rounded-[10px] bg-surface-sunken px-3 text-[15px] font-semibold"
                >
                    <NotebookPen size={18} strokeWidth={1.6} aria-hidden="true" className="text-brand" />
                    이모 수첩에 적기
                </button>
                <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                        onClose();
                        navigator.clipboard?.writeText(body).catch(() => {});
                    }}
                    className="flex h-11 items-center gap-2 rounded-[10px] px-3 text-[15px]"
                >
                    <Copy size={18} strokeWidth={1.6} aria-hidden="true" />
                    복사
                </button>
            </div>
        </div>,
        document.body
    );
}

export function MyMessage({ body, status = 'sent', onRetry = null, onDiscard = null, onNote = null }) {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const pressHandlers = useLongPress(onNote && status === 'sent' ? setMenuAnchor : null);
    const failed = status === 'failed';
    let dim = '';
    if (status === 'sending') {
        dim = 'opacity-60';
    } else if (failed) {
        dim = 'opacity-70';
    }
    return (
        <div className="flex flex-col items-end gap-1.5 motion-safe:animate-message-in">
            <div className="flex max-w-full items-center justify-end gap-2">
                {failed ? (
                    <CircleAlert size={18} strokeWidth={1.6} className="shrink-0 text-error" aria-hidden="true" />
                ) : null}
                <p
                    {...pressHandlers}
                    className={`m-0 max-w-bubble rounded-bubble rounded-tr-bubble-tail bg-surface-sunken px-3.5 py-2.5 text-body break-words whitespace-pre-wrap select-text ${dim} ${
                        menuAnchor ? 'relative z-[60] ring-[1.5px] ring-brand ring-offset-2 ring-offset-white' : ''
                    }`}
                >
                    <span className="sr-only">내가 말함: </span>
                    <MentionedBody body={body} />
                </p>
            </div>
            {onNote && status === 'sent' ? (
                <button type="button" onClick={onNote} className="sr-only">
                    이 말 이모 수첩에 적기
                </button>
            ) : null}
            {menuAnchor ? (
                <MyMessageMenu anchor={menuAnchor} body={body} onNote={onNote} onClose={() => setMenuAnchor(null)} />
            ) : null}
            {failed ? (
                <div className="flex items-center gap-3">
                    <span role="alert" className="text-[12px] leading-4 text-error">
                        못 보냈어요
                    </span>
                    {onRetry ? (
                        <button
                            type="button"
                            onClick={onRetry}
                            className="flex h-8 items-center gap-1 rounded-2xl border border-brand px-3 text-[12px] font-semibold text-brand"
                        >
                            <RotateCcw size={14} strokeWidth={1.6} aria-hidden="true" />
                            다시 보내기
                        </button>
                    ) : null}
                    {onDiscard ? (
                        <button
                            type="button"
                            onClick={onDiscard}
                            className="flex min-h-touch items-center px-1 text-[12px] text-text-secondary"
                        >
                            지우기
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
